<template>
  <validation-observer ref="observer" v-slot="{ invalid }">
    <v-card class="pb-4" outlined>
      <v-card-title class="py-1" :style="{ 'background-color': '#eeeeee' }">
        {{ $t('MSG0080') }}
      </v-card-title>
      <v-card-text class="py-1">
        <v-container class="form-sa-epson px-2 py-2" fluid>
          <v-row>
            <v-col cols="2" class="py-0">
              <Checkbox :label="$t('ELE0239')" v-model="form.isCriticalPart" :disabled="!canEditContents" />
            </v-col>
          </v-row>
          <v-row class="my-0">
            <v-col>
              <v-card class="pb-2" elevation="1" max-width="1200px" outlined>
                <v-card-title class="py-1">
                  {{ $t('ELE0240') }}
                  <v-spacer />
                  <BtnAdd :disabled="!canEditContents" @click="onAddDoc" />
                </v-card-title>
                <v-card class="mx-2" outlined>
                  <v-data-table
                    :headers="headersDoc"
                    :items="requiredDocs"
                    dense
                    disable-pagination
                    hide-default-footer
                    mobile-breakpoint="0"
                  >
                    <template v-slot:[`item.action`]="{ index }">
                      <IconDelete :disabled="!canEditContents" @click="onDeleteDoc(index)" />
                    </template>
                    <template v-slot:[`item.docCatCd`]="{ item, index }">
                      <SaRequiredDocInput
                        :vid="'docCatCd_' + index"
                        label=""
                        v-model="item.docCatCd"
                        :exceptCdList="selectedDocCats"
                        :validOnly="canEditContents"
                        required
                        :disabled="!canEditContents"
                      />
                    </template>
                    <template v-slot:[`item.remarks`]="{ item }">
                      <TextFieldInput v-if="canEditContents" label="" v-model="item.remarks" :maxLength="1000" />
                      <TextFieldRef v-else label="" :value="item.remarks" />
                    </template>
                    <template v-slot:[`item.file`]="{ item, index }">
                      <FileInput
                        :vid="item.filePath + index"
                        label=""
                        v-model="item.file"
                        :filePath="item.filePath"
                        required
                      />
                    </template>
                  </v-data-table>
                </v-card>
              </v-card>
            </v-col>
          </v-row>
          <v-row class="my-0">
            <v-col>
              <v-card class="pb-2" elevation="1" max-width="1200px" outlined>
                <v-card-title class="py-1">
                  {{ $t('ELE0354') }}
                  <v-spacer />
                  <BtnAdd :disabled="!canEditContents" @click="onAddModel" />
                </v-card-title>
                <v-card class="mx-2" outlined>
                  <v-data-table
                    :headers="headersModel"
                    :items="modelPlants"
                    dense
                    disable-pagination
                    hide-default-footer
                    clearable
                  >
                    <template v-slot:[`item.action`]="{ index }">
                      <IconDelete :disabled="!canEditContents" @click="onDeleteModel(index)" />
                    </template>
                    <template v-slot:[`item.model`]="{ item }">
                      <TextFieldInput
                        v-if="canEditContents"
                        label=""
                        v-model="item.model"
                        rules="uniqueModel"
                        :maxLength="80"
                        required
                      />
                      <TextFieldRef v-else label="" :value="item.model" />
                    </template>
                    <template v-slot:[`item.companyCd`]="{ item, index }">
                      <CompanyInput
                        :vid="`companyCd_${index}`"
                        label=""
                        v-model="item.companyCd"
                        :companyCatCd="enumCompanyCatCd.EPSON"
                        required
                        validOnly
                        :disabled="!canEditContents"
                      />
                    </template>
                    <template v-slot:[`item.judge`]="{ item }">
                      <SelectSingleInput
                        label=""
                        v-model="item.judge"
                        :items="modelPlantJudgeItems"
                        itemText="name"
                        itemValue="value"
                        :required="isFinalJudgeUser"
                        :disabled="!canApprove"
                      />
                    </template>
                  </v-data-table>
                </v-card>
              </v-card>
            </v-col>
          </v-row>
          <v-row class="my-0">
            <v-col>
              <v-card elevation="1" max-width="1200px" outlined>
                <v-card-title class="py-1">
                  {{ $t('ELE0219') }} {{ currentRouteTitle }}
                  <v-spacer />
                  <BtnDelete class="mx-2" :disabled="!canDeleteRoute" @click="onDeleteRoute" />
                  <BtnAdd :disabled="!canAddRoute" @click="onAddRoute" />
                </v-card-title>
                <v-card outlined>
                  <v-tabs fixed-tabs v-model="routeTab">
                    <v-tab
                      class="ml-0"
                      v-for="(item, index) in epsonRoutes"
                      :key="item.routeNo"
                      :href="`#${item.routeNo}`"
                    >
                      <span>{{ index + 1 }}</span>
                    </v-tab>
                  </v-tabs>
                  <v-tabs-items v-model="routeTab">
                    <v-tab-item v-for="item in epsonRoutes" :key="item.routeNo" :value="`${item.routeNo}`">
                      <SaRequestRouteForm
                        :vid="item.routeNo"
                        isEpsonRoute
                        :companyCd="form.acceptCompanyCd"
                        :companyCatCd="enumCompanyCatCd.EPSON"
                        :currentRouteNo="form.currentRouteNo"
                        :currentRouteDtlNo="form.currentRouteDtlNo"
                        :routeNo="item.routeNo"
                        :deptCd.sync="item.deptCd"
                        :checkers.sync="item.checkers"
                        :evidences.sync="item.evidences"
                        :disabled="!canEditRoute || item.routeNo < form.currentRouteNo"
                      />
                    </v-tab-item>
                  </v-tabs-items>
                </v-card>
              </v-card>
            </v-col>
          </v-row>
          <v-container fluid class="px-0" v-if="isCmsRoute">
            <v-row class="my-0">
              <v-col>
                <v-card elevation="1" max-width="1200px" outlined>
                  <v-card-title class="py-1">
                    {{ $t('ELE0508') }} {{ cmsCurrentRouteTitle }}
                    <v-spacer />
                    <BtnDelete class="mx-2" :disabled="!canDeleteCmsRoute" @click="onDeleteCmsRoute" />
                    <BtnAdd :disabled="!canAddCmsRoute" @click="onAddCmsRoute" />
                  </v-card-title>
                  <v-card outlined>
                    <v-tabs fixed-tabs v-model="cmsRouteTab">
                      <v-tab
                        class="ml-0"
                        v-for="(item, index) in cmsEpsonRoutes"
                        :key="item.routeNo"
                        :href="`#${item.routeNo}`"
                      >
                        <span>{{ index + 1 }}</span>
                      </v-tab>
                    </v-tabs>
                    <v-tabs-items v-model="cmsRouteTab">
                      <v-tab-item v-for="item in cmsEpsonRoutes" :key="item.routeNo" :value="`${item.routeNo}`">
                        <SaRequestRouteForm
                          :vid="item.routeNo"
                          isEpsonRoute
                          :companyCd="form.acceptCompanyCd"
                          :companyCatCd="enumCompanyCatCd.EPSON"
                          :currentRouteNo="form.currentRouteNo"
                          :currentRouteDtlNo="form.currentRouteDtlNo"
                          :routeNo="item.routeNo"
                          :deptCd.sync="item.deptCd"
                          :checkers.sync="item.checkers"
                          :evidences.sync="item.evidences"
                          :disabled="!canEditCmsRoute || item.routeNo < form.currentRouteNo"
                        />
                      </v-tab-item>
                    </v-tabs-items>
                  </v-card>
                </v-card>
              </v-col>
            </v-row>
            <v-row class="my-0">
              <v-col dense>
                <v-card class="pb-2" elevation="1" max-width="1200px" outlined>
                  <v-card-title class="py-1">
                    {{ $t('ELE0509') }}
                    <v-spacer />
                    <BtnAdd :disabled="!canEditCmsEff" @click="onAddCmsEff" />
                  </v-card-title>
                  <v-card class="mx-2" outlined>
                    <v-data-table
                      :headers="headersCmsEff"
                      :items="cmsEffs"
                      dense
                      disable-pagination
                      hide-default-footer
                      mobile-breakpoint="0"
                    >
                      <template v-slot:[`item.action`]="{ index }">
                        <IconDelete :disabled="!canEditCmsEff" @click="onDeleteCmsEff(index)" />
                      </template>
                      <template v-slot:[`item.deptCd`]="{ item, index }">
                        <DepartmentInput
                          :vid="'deptCd_' + index"
                          label=""
                          v-model="item.deptCd"
                          :validOnly="canEditCmsEff"
                          :disabled="!canEditCmsEff"
                          required
                        />
                      </template>
                      <template v-slot:[`item.defectRate`]="{ item }">
                        <TextFieldInput
                          v-if="canEditCmsEff"
                          label=""
                          v-model="item.defectRate"
                          :maxLength="20"
                          :required="canEditCmsEff"
                          :disabled="!canEditCmsEff"
                        />
                        <TextFieldRef v-else label="" :value="item.defectRate" />
                      </template>
                      <template v-slot:[`item.judge`]="{ item }">
                        <SelectSingleInput
                          label=""
                          v-model="item.judge"
                          :items="cmsEffJudgeItems"
                          itemText="name"
                          itemValue="value"
                          :required="canEditCmsEff"
                          :disabled="!canEditCmsEff"
                        />
                      </template>
                      <template v-slot:[`item.remarks`]="{ item }">
                        <TextFieldInput v-if="canEditCmsEff" label="" v-model="item.remarks" :maxLength="1000" />
                        <TextFieldRef v-else label="" :value="item.remarks" />
                      </template>
                    </v-data-table>
                  </v-card>
                </v-card>
              </v-col>
            </v-row>
          </v-container>
          <v-row>
            <v-col cols="4">
              <SaDistrDestMultipleInput
                v-model="form.distDestCds"
                :validOnly="canEditContents"
                :disabled="!canEditContents"
              />
            </v-col>
            <v-col cols="3">
              <TextFieldInputWithDatePicker
                :label="$t('ELE0254')"
                v-model="form.endDate"
                disablePastDate
                :disabled="!canEditContents"
                required
              />
            </v-col>
          </v-row>
          <v-row v-if="canEditRoute || canEditCmsRoute">
            <v-col>
              <v-container v-if="!isCmsRoute" fluid class="px-0 d-flex justify-end">
                <Btn
                  class="mx-4"
                  :label="$t('ELE0215')"
                  :disabled="invalid || !canApprove"
                  isCheckRole
                  @click="onClickApprove"
                />
                <Btn
                  class="mx-4"
                  :label="$t('ELE0249')"
                  :disabled="invalid || !canDeny"
                  isCheckRole
                  @click="onClickDeny"
                />
                <Btn
                  class="mx-4"
                  :label="$t('ELE0250')"
                  :disabled="!canReturnToRequest"
                  isCheckRole
                  @click="onClickReturnToRequest"
                />
                <Btn
                  class="mx-4"
                  :label="$t('ELE0375')"
                  :disabled="!canReturnToSupplier"
                  isCheckRole
                  @click="onClickReturnToSupplier"
                />
              </v-container>
              <v-container v-else fluid class="px-0 d-flex justify-end">
                <Btn
                  class="mx-4"
                  :label="$t('ELE0215')"
                  :disabled="invalid || !canCmsApprove"
                  isCheckRole
                  @click="onClickApproveCms"
                />
                <Btn class="mx-4" :label="$t('ELE0249')" disabled />
                <Btn
                  class="mx-4"
                  :label="$t('ELE0250')"
                  :disabled="!canReturnToRequestCms"
                  isCheckRole
                  @click="onClickReturnToRequestCms"
                />
                <Btn
                  class="mx-4"
                  :label="$t('ELE0375')"
                  :disabled="!canReturnToSupplierCms"
                  isCheckRole
                  @click="onClickReturnToSupplierCms"
                />
              </v-container>
            </v-col>
          </v-row>
        </v-container>
      </v-card-text>
    </v-card>

    <UserConfirmDialog
      :dialog="userConfirmDialog"
      :confirmWord="$t('MSG0090')"
      :btnLabel="$t('ELE0019')"
      @cancel="userConfirmDialog = false"
      @proceed="onProceedSave"
    />
  </validation-observer>
</template>

<script>
import { ValidationObserver, extend } from 'vee-validate'
import { API } from '@/plugins/api'
import APIUtils from '@/mixin/api-utils.js'
import Message from '@/mixin/message-utils.js'
import Loading from '@/mixin/loading-utils.js'
import StringUtils from '@/mixin/string-utils.js'
import { Storage } from '@/plugins/storage'
import {
  Role,
  SaWfCheckedCat,
  SaApprovalStatusCd,
  S3Root,
  CompanyCatCd,
  ModelPlantJudgeCd,
  CmsEffJudgeCd,
  CmsApprovalStatusCd,
} from '@/enum'

import TextFieldInput from '@/components/atoms/base/text-field/TextFieldInput'
import TextFieldRef from '@/components/atoms/base/text-field/TextFieldRef'
import TextFieldInputWithDatePicker from '@/components/atoms/base/text-field/TextFieldInputWithDatePicker'
import Checkbox from '@/components/atoms/base/checkbox/Checkbox'
import Btn from '@/components/atoms/base/button/Btn'
import BtnAdd from '@/components/atoms/button/BtnAdd'
import BtnDelete from '@/components/atoms/button/BtnDelete'
import IconDelete from '@/components/atoms/icon/IconDelete.vue'
import FileInput from '@/components/atoms/file/FileInput'

import SaRequiredDocInput from '@/components/atoms/select/SaRequiredDocInput'
import SaDistrDestMultipleInput from '@/components/atoms/select/SaDistrDestMultipleInput'

import SelectSingleInput from '@/components/atoms/base/select/SelectSingleInput'
import CompanyInput from '@/components/atoms/select/CompanyInput'
import DepartmentInput from '@/components/atoms/select/DepartmentInput'

import SaRequestRouteForm from '@/components/organisms/terminal/form/SaRequestRouteForm'
import UserConfirmDialog from '@/components/organisms/common/dialog/ConfirmDialog'

/** 処理モード */
const SaveMode = {
  REQUEST: 'REQUEST',
  APPROVE: 'APPROVE',
  DENY: 'DENY',
  RETURN: 'RETURN',
  CMS_START_CMS_ROUTE: 'CMS_START_CMS_ROUTE',
  CMS_REQUEST: 'CMS_REQUEST',
  CMS_APPROVE: 'CMS_APPROVE',
  CMS_RETURN: 'CMS_RETURN',
  CMS_RETURN_TO_SUPPLIER: 'CMS_RETURN_TO_SUPPLIER',
}

/** SA WFのルートNo */
const RouteNo = {
  EPSON_START: 2,
}

const MaxCountRoute = 10

export default {
  name: 'SaRequestEpsonForm',

  mixins: [APIUtils, Message, Loading, StringUtils],

  components: {
    ValidationObserver,
    TextFieldInput,
    TextFieldRef,
    TextFieldInputWithDatePicker,
    Checkbox,
    Btn,
    BtnAdd,
    BtnDelete,
    IconDelete,
    FileInput,
    SaRequiredDocInput,
    SaDistrDestMultipleInput,
    SelectSingleInput,
    CompanyInput,
    DepartmentInput,
    SaRequestRouteForm,
    UserConfirmDialog,
  },

  props: {
    form: {
      type: Object,
      required: true,
    },
    canEditEpson: {
      type: Boolean,
      required: true,
    },
    canApproveEpson: {
      type: Boolean,
      required: true,
    },
    canReturnEpson: {
      type: Boolean,
      required: true,
    },
    isCmsRoute: {
      type: Boolean,
      default: false,
      required: false,
    },
  },

  data() {
    return {
      routeTab: '',
      cmsRouteTab: '',
      countAdd: 0,
      countAddCms: 0,
      headersDoc: [
        { text: '', value: 'action', align: 'center', divider: true, sortable: false, width: '50' },
        {
          text: this.$t('ELE0241'),
          value: 'docCatCd',
          divider: true,
          sortable: false,
          cellClass: 'py-1',
        },
        { text: this.$t('ELE0054'), value: 'remarks', sortable: false, cellClass: 'py-1' },
        { text: this.$t('ELE0035'), value: 'file', sortable: false, cellClass: 'py-1' },
      ],
      headersModel: [
        { text: '', value: 'action', align: 'center', divider: true, sortable: false, width: '50' },
        {
          text: this.$t('ELE0224'),
          value: 'model',
          divider: true,
          sortable: false,
          cellClass: 'py-1',
        },
        { text: this.$t('ELE0355'), value: 'companyCd', sortable: false, cellClass: 'py-1' },
        { text: this.$t('ELE0181'), value: 'judge', sortable: false, cellClass: 'py-1', width: 180 },
      ],
      headersCmsEff: [
        { text: '', value: 'action', align: 'center', divider: true, sortable: false, width: '50' },
        { text: this.$t('ELE0242'), value: 'deptCd', sortable: false, cellClass: 'py-1' },
        { text: this.$t('ELE0315'), value: 'defectRate', sortable: false, cellClass: 'py-1' },
        { text: this.$t('ELE0181'), value: 'judge', sortable: false, cellClass: 'py-1', width: 180 },
        { text: this.$t('ELE0054'), value: 'remarks', sortable: false, cellClass: 'py-1' },
      ],
      mode: '',
      settings: [
        {
          mode: SaveMode.REQUEST,
          url: '/sa/epson/request',
          isUploadEvidFile: true,
          isUploadReqDoc: true,
          isValidate: true,
        },
        {
          mode: SaveMode.APPROVE,
          url: '/sa/epson/approve',
          isUploadEvidFile: true,
          isUploadReqDoc: false,
          isValidate: true,
        },
        {
          mode: SaveMode.DENY,
          url: '/sa/epson/deny',
          isUploadEvidFile: true,
          isUploadReqDoc: false,
          isValidate: true,
        },
        {
          mode: SaveMode.RETURN,
          url: '/sa/epson/return',
          isUploadEvidFile: false,
          isUploadReqDoc: false,
          isValidate: false,
        },
        {
          mode: SaveMode.RETURN_TO_SUPPLIER,
          url: '/sa/epson/returnToSupplier',
          isUploadEvidFile: false,
          isUploadReqDoc: false,
          isValidate: false,
        },
        {
          mode: SaveMode.CMS_START_CMS_ROUTE,
          url: '/sa/epson/cms/startRoute',
          isUploadEvidFile: true,
          isUploadReqDoc: false,
          isValidate: true,
        },
        {
          mode: SaveMode.CMS_REQUEST,
          url: '/sa/epson/cms/request',
          isUploadEvidFile: true,
          isUploadReqDoc: false,
          isValidate: true,
        },
        {
          mode: SaveMode.CMS_APPROVE,
          url: '/sa/epson/cms/approve',
          isUploadEvidFile: true,
          isUploadReqDoc: false,
          isValidate: true,
        },
        {
          mode: SaveMode.CMS_RETURN,
          url: '/sa/epson/cms/return',
          isUploadEvidFile: false,
          isUploadReqDoc: false,
          isValidate: false,
        },
        {
          mode: SaveMode.CMS_RETURN_TO_SUPPLIER,
          url: '/sa/epson/cms/returnToSupplier',
          isUploadEvidFile: false,
          isUploadReqDoc: false,
          isValidate: false,
        },
      ],
      userConfirmDialog: false,
    }
  },

  computed: {
    enumCompanyCatCd: () => CompanyCatCd,
    canEditEpsonComputed: {
      get() {
        return this.canEditEpson
      },
      set(value) {
        this.$emit('update:canEditEpson', value)
      },
    },
    canApproveEpsonComputed: {
      get() {
        return this.canApprove
      },
      set(value) {
        this.$emit('update:canApproveEpson', value)
      },
    },
    canReturnEpsonComputed: {
      get() {
        return this.canReturn
      },
      set(value) {
        this.$emit('update:canReturnEpson', value)
      },
    },
    loginUserCd: {
      get() {
        return this.$store.getters.userCd
      },
    },
    setting: {
      get() {
        return this.settings.find((el) => el.mode === this.mode)
      },
    },
    canDeleteRoute: {
      get() {
        let result = true
        if (!this.canEditRoute || this.routeTab <= this.form.currentRouteNo || this.isFirstRoute(this.routeTab)) {
          result = false
        }
        return result
      },
    },
    canDeleteCmsRoute: {
      get() {
        let result = true
        if (
          !this.canEditCmsRoute ||
          this.cmsRouteTab <= this.form.currentRouteNo ||
          this.isFirstRoute(this.cmsRouteTab)
        ) {
          result = false
        }
        return result
      },
    },
    canAddRoute: {
      get() {
        let result = true
        if (!this.canEditRoute || this.epsonRoutes.length >= MaxCountRoute) {
          result = false
        }
        return result
      },
    },
    canAddCmsRoute: {
      get() {
        let result = true
        if (!this.canEditCmsRoute || this.cmsEpsonRoutes.length >= MaxCountRoute) {
          result = false
        }
        return result
      },
    },
    canEditCmsEff: {
      get() {
        return this.canEditCmsContents && this.form.cmsStatusCd == CmsApprovalStatusCd.SUPPLIER_APPROVED
      },
    },
    canEditCompany: {
      get() {
        return this.$store.getters.isEpson
      },
    },
    canEditContents: {
      get() {
        return this.canEditCompany && this.form.statusCd == SaApprovalStatusCd.SUPPLIER_APPROVED
      },
    },
    canEditCmsContents: {
      get() {
        return this.canEditCompany && this.isCmsRoute && this.form.cmsStatusCd == CmsApprovalStatusCd.SUPPLIER_APPROVED
      },
    },
    canRequest: {
      get() {
        return this.canEditContents
      },
    },
    canRequestCms: {
      get() {
        return this.canEditCmsContents
      },
    },
    canEditRoute: {
      get() {
        return this.canRequest || this.canApprove || this.canReturnToRequest
      },
    },
    canEditCmsRoute: {
      get() {
        return this.canRequestCms || this.canCmsApprove || this.canReturnToRequestCms
      },
    },
    canApprove: {
      get() {
        return this.canEditCompany && !this.isCmsRoute && this.hasEditRole
      },
    },
    canCmsApprove: {
      get() {
        return this.canEditCompany && this.hasEditRole
      },
    },
    hasEditRole: {
      get() {
        let result = false
        const currentCheckedCat = this.form.currentCheckedCat
        const hasCheck = this.$store.getters.hasRole(Role.SA_CHECK)
        const hasApprove = this.$store.getters.hasRole(Role.SA_APPROVE)
        if (currentCheckedCat == SaWfCheckedCat.REQUEST && (hasCheck || hasApprove)) {
          result = true
        } else if (currentCheckedCat == SaWfCheckedCat.CHECK && hasCheck) {
          result = true
        } else if (currentCheckedCat == SaWfCheckedCat.APPROVE && hasApprove) {
          result = true
        } else {
          result = false
        }
        return result
      },
    },
    canDeny: {
      get() {
        return this.canApprove
      },
    },
    canReturn: {
      get() {
        const hasCheck = this.$store.getters.hasRole(Role.SA_CHECK)
        const hasApprove = this.$store.getters.hasRole(Role.SA_APPROVE)
        return this.canEditCompany && (hasCheck || hasApprove)
      },
    },
    canReturnToRequest: {
      get() {
        return this.canReturn && this.form.statusCd == SaApprovalStatusCd.EPSON_APPLYING
      },
    },
    canReturnToRequestCms: {
      get() {
        return this.canReturn && this.form.cmsStatusCd == CmsApprovalStatusCd.EPSON_APPLYING
      },
    },
    canReturnToSupplier: {
      get() {
        return this.canReturn && this.form.statusCd == SaApprovalStatusCd.SUPPLIER_APPROVED
      },
    },
    canReturnToSupplierCms: {
      get() {
        return this.canReturn && this.form.cmsStatusCd == CmsApprovalStatusCd.SUPPLIER_APPROVED
      },
    },
    requiredDocs: {
      get() {
        return this.form.requiredDocs
      },
    },
    modelPlants: {
      get() {
        return this.form.modelPlants
      },
    },
    epsonRoutes: {
      get() {
        return this.form.epsonRoutes
      },
      set(value) {
        this.form.epsonRoutes = value
      },
    },
    currentRouteTitle: {
      get() {
        let title = ''
        for (let idx = 0; idx < this.epsonRoutes.length; idx++) {
          if (this.epsonRoutes[idx].routeNo == this.form.currentRouteNo) {
            title = '(' + this.$t('ELE0328') + ' : ' + String(idx + 1) + ')'
          }
        }
        return title
      },
    },
    cmsEpsonRoutes: {
      get() {
        return this.form.cmsEpsonRoutes
      },
      set(value) {
        this.form.cmsEpsonRoutes = value
      },
    },
    cmsCurrentRouteTitle: {
      get() {
        let title = ''
        for (let idx = 0; idx < this.cmsEpsonRoutes.length; idx++) {
          if (this.cmsEpsonRoutes[idx].routeNo == this.form.currentRouteNo) {
            title = '(' + this.$t('ELE0328') + ' : ' + String(idx + 1) + ')'
          }
        }
        return title
      },
    },
    cmsEffs: {
      get() {
        return this.form.cmsEffs
      },
    },
    selectedDocCats: {
      // 選択済みのドキュメントカテゴリリスト
      get() {
        let items = []
        for (let idx = 0; idx < this.requiredDocs.length; idx++) {
          const doc = this.requiredDocs[idx]
          items.push({ docCatCd: doc.docCatCd })
        }
        return items
      },
    },
    inputtedModels: {
      // 選択済みの機種_プラント モデルリスト
      get() {
        let items = []
        for (let idx = 0; idx < this.modelPlants.length; idx++) {
          const e = this.modelPlants[idx]
          items.push(e.model)
        }
        return items
      },
    },
    modelPlantJudgeItems: {
      get() {
        // プルダウン選択肢をenumから生成
        return Object.values(ModelPlantJudgeCd).map((item) => {
          return { name: this.$t(`ModelPlantJudgeCd.${item}`), value: item }
        })
      },
    },
    cmsEffJudgeItems: {
      get() {
        // プルダウン選択肢をenumから生成
        return Object.values(CmsEffJudgeCd).map((item) => {
          return { name: this.$t(`CmsEffJudgeCd.${item}`), value: item }
        })
      },
    },
    isFinalJudgeUser: {
      get() {
        // 最終判定者が操作しているか
        let finalRoute = null
        let finalRouteDtl = null
        finalRoute = this.form.epsonRoutes.slice(-1)[0]
        finalRouteDtl = finalRoute.checkers.slice(-1)[0] // 最終ルート
        return (
          finalRouteDtl.routeNo == this.form.currentRouteNo && finalRouteDtl.routeDtlNo == this.form.currentRouteDtlNo
        )
      },
    },
    cmsStartRouteNo: {
      get() {
        return this.cmsEpsonRoutes[0].routeNo
      },
    },
  },

  methods: {
    isFirstRoute(routeNo) {
      return routeNo == RouteNo.EPSON_START || (this.isCmsRoute && routeNo == this.cmsEpsonRoutes[0].routeNo)
    },
    onAddDoc() {
      const item = {
        docCatCd: '',
        remarks: '',
      }
      this.requiredDocs.push(item)
    },
    onAddModel() {
      const item = {
        model: '',
        companyCd: '',
        judge: '',
      }
      this.modelPlants.push(item)
    },
    onAddCmsEff() {
      const item = {
        deptCd: '',
        defectRate: '',
        judge: '',
        remarks: '',
      }
      this.cmsEffs.push(item)
    },
    onDeleteDoc(index) {
      this.requiredDocs.splice(index, 1)
    },
    onDeleteModel(index) {
      this.modelPlants.splice(index, 1)
    },
    onDeleteCmsEff(index) {
      this.cmsEffs.splice(index, 1)
    },
    async onAddRoute() {
      this.countAdd++

      const item = {
        routeNo: this.countAdd + MaxCountRoute,
        deptCd: '',
        checkers: [
          {
            checkerCd: '',
            checkedDatetime: '',
            remarks: '',
            checkedCat: SaWfCheckedCat.REQUEST,
          },
          {
            checkerCd: '',
            checkedDatetime: '',
            remarks: '',
            checkedCat: SaWfCheckedCat.CHECK,
          },
          {
            checkerCd: '',
            checkedDatetime: '',
            remarks: '',
            checkedCat: SaWfCheckedCat.APPROVE,
          },
        ],
        evidences: [],
      }
      this.epsonRoutes.splice(this.epsonRoutes.length + 1, 0, item)
      await this.refreshTab(this.routeTab)
    },
    onDeleteRoute() {
      this.epsonRoutes = this.epsonRoutes.filter((m) => m.routeNo != this.routeTab)
    },
    async onAddCmsRoute() {
      this.countAddCms++

      const item = {
        routeNo: this.countAddCms + MaxCountRoute,
        deptCd: '',
        checkers: [
          {
            checkerCd: '',
            checkedDatetime: '',
            remarks: '',
            checkedCat: SaWfCheckedCat.REQUEST,
          },
          {
            checkerCd: '',
            checkedDatetime: '',
            remarks: '',
            checkedCat: SaWfCheckedCat.CHECK,
          },
          {
            checkerCd: '',
            checkedDatetime: '',
            remarks: '',
            checkedCat: SaWfCheckedCat.APPROVE,
          },
        ],
        evidences: [],
      }
      this.cmsEpsonRoutes.splice(this.cmsEpsonRoutes.length + 1, 0, item)
      await this.refreshTab(this.cmsRouteTab)
    },
    onDeleteCmsRoute() {
      this.cmsEpsonRoutes = this.cmsEpsonRoutes.filter((m) => m.routeNo != this.cmsRouteTab)
    },
    onClickApprove() {
      // 承認
      if (this.canRequest) {
        this.mode = SaveMode.REQUEST
      } else if (this.isFinalJudgeUser && this.form.reqCms) {
        this.mode = SaveMode.CMS_START_CMS_ROUTE
      } else {
        this.mode = SaveMode.APPROVE
      }
      this._save()
    },
    onClickApproveCms() {
      // 承認
      if (this.canRequestCms) {
        this.mode = SaveMode.CMS_REQUEST
      } else {
        this.mode = SaveMode.CMS_APPROVE
      }

      this._save()
    },
    onClickDeny() {
      // 否認
      this.mode = SaveMode.DENY
      this._save()
    },
    onClickReturnToRequest() {
      // 申請者へ戻す
      this.mode = SaveMode.RETURN
      this._save()
    },
    onClickReturnToRequestCms() {
      // Counter Measures申請者へ戻す
      this.mode = SaveMode.CMS_RETURN
      this._save()
    },
    onClickReturnToSupplier() {
      // サプライヤーへ戻す
      this.mode = SaveMode.RETURN_TO_SUPPLIER
      this._save()
    },
    onClickReturnToSupplierCms() {
      // Counter Measuresサプライヤーへ戻す
      this.mode = SaveMode.CMS_RETURN_TO_SUPPLIER
      this._save()
    },
    async onProceedSave() {
      // 保存を進める
      this.userConfirmDialog = false
      let checker = this.getCurrentChecker()
      checker.checkerCd = this.loginUserCd
      await this._save()
    },
    async _save() {
      // 保存
      try {
        this.startLoading()

        if (this.setting.isValidate && !(await this.validate())) {
          return
        }

        if (this.setting.isUploadEvidFile) {
          await this.uploadEvidFile()
        }
        if (this.setting.isUploadReqDoc) {
          await this.uploadReqDocFile()
        }

        const body = {
          saNo: this.form.saNo,
          wfNo: this.form.wfNo,
          wfVersionNo: this.form.wfVersionNo,
          currentRouteNo: this.form.currentRouteNo,
          currentRouteDtlNo: this.form.currentRouteDtlNo,
          acceptCompanyCd: this.form.acceptCompanyCd,
          isCriticalPart: this.form.isCriticalPart,
          requiredDocs: this.requiredDocs,
          modelPlants: this.modelPlants,
          distDestCds: this.form.distDestCds,
          endDate: this.form.endDate,
          epsonRoutes: this.epsonRoutes,
          cmsEpsonRoutes: this.cmsEpsonRoutes,
          cmsEffs: this.cmsEffs,
          nextAction: this.form.nextAction,
          saDims: this.form.saDims,
          requestComment: this.getCurrentChecker().remarks,
          // WAFエラー回避のためLinkをエンコードしてPost
          relatedLinks: this.form.relatedLinks.map((item) => ({
            ...item,
            link: this.toBase64(item.link),
          })),
        }

        await API.post(this.setting.url, body)
        this.setSuccessMessage(this.$t('MSG0011', { p0: this.form.saNo }))
        this.resetForm()
      } catch (error) {
        this.setErrorMessage(this.resolveErrorMessage(error, this.$t('MSG0003', { p0: this.form.saNo })))
      } finally {
        this.stopLoading()
      }
    },
    async validate() {
      // 入力チェック
      if (!(await this.$refs.observer.validate())) {
        return false
      }

      if (this.existsUserInRoute()) {
        this.setErrorMessage(this.$t('MSG0094'))
        return false
      }

      const checker = this.getCurrentChecker()
      if (checker && checker.checkerCd != this.loginUserCd) {
        this.userConfirmDialog = true
        return false
      }

      return true
    },
    getCurrentRoute() {
      // 現在の処理ルート取得
      return [...this.form.epsonRoutes, ...this.form.cmsEpsonRoutes].find(
        (el) => el.routeNo == this.form.currentRouteNo,
      )
    },
    existsUserInRoute() {
      // 現在ルート詳細No以外で、ログインユーザーが指定されているかチェック
      return this.getCurrentRoute().checkers.some(
        (el) => el.routeDtlNo != this.form.currentRouteDtlNo && el.checkerCd == this.loginUserCd,
      )
    },
    getCurrentChecker() {
      // WFで指定されている現在の処理ユーザー取得
      return this.getCurrentRoute().checkers.find((el) => el.routeDtlNo == this.form.currentRouteDtlNo)
    },
    async uploadEvidFile() {
      // 確認エビデンスファイルをS3へアップロード
      const routes = [...this.epsonRoutes, ...this.cmsEpsonRoutes]
      for (let idxRoute = 0; idxRoute < routes.length; idxRoute++) {
        let route = routes[idxRoute]
        for (let idxExam = 0; idxExam < route.evidences.length; idxExam++) {
          let exam = route.evidences[idxExam]
          if (!exam.filePath) {
            exam.filePath = await Storage.uploadFile(exam.file, 'sa/' + this.form.saNo + '/' + S3Root.SA_EVIDENCE)
            exam.file = null
          }
        }
      }
    },
    async uploadReqDocFile() {
      // 必要ドキュメントをS3へアップロード
      for (let idxReqDoc = 0; idxReqDoc < this.requiredDocs.length; idxReqDoc++) {
        const requiredDoc = this.requiredDocs[idxReqDoc]
        if (!requiredDoc.filePath) {
          requiredDoc.filePath = await Storage.uploadFile(
            requiredDoc.file,
            'sa/' + this.form.saNo + '/' + S3Root.SA_REQUIRE_DOC,
          )
          requiredDoc.file = null
        }
      }
    },
    resetForm() {
      this.$emit('reset')
    },
    async refreshTab(tabNo) {
      // 全タブのvee-validateを走らせるために、全タブを1度activeにする。
      if (this.isCmsRoute) {
        for (let idx = 0; idx < this.cmsEpsonRoutes.length; idx++) {
          this.cmsRouteTab = String(this.cmsEpsonRoutes[idx].routeNo)
          await this.$nextTick()
        }
        this.cmsRouteTab = String(tabNo)
      } else {
        for (let idx = 0; idx < this.epsonRoutes.length; idx++) {
          this.routeTab = String(this.epsonRoutes[idx].routeNo)
          await this.$nextTick()
        }
        this.routeTab = String(tabNo)
      }
    },
  },
  async mounted() {
    extend('uniqueModel', {
      validate: (value) => {
        let result = false
        if (value == '') {
          result = true
        } else {
          if (this.inputtedModels.filter((e) => e == value).length >= 2) {
            result = this.$t('MSG0031', { p0: value })
          } else {
            result = true
          }
        }
        return result
      },
    })

    await this.refreshTab(this.form.currentRouteNo)

    this.canEditEpsonComputed = this.canEditRoute || this.canEditCmsRoute
    this.canApproveEpsonComputed = this.canApprove || this.canCmsApprove
    this.canReturnEpsonComputed = this.canReturn
  },
}
</script>
<style>
.form-sa-epson .row > .col {
  padding: 6px;
}
</style>
